import { combineReducers, applyMiddleware, createStore, compose } from 'redux';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { reducer as formReducer } from 'redux-form';
import thunk from 'redux-thunk';
import reduxCatch from 'redux-catch';

import { merge, pick } from 'lodash';

import * as localStore from './utils/localStorage';
import * as sessionStore from './utils/sessionStorage';

import projects from './services/projects/reducer';
import project from './services/project/reducer';
import environments from './services/environments/reducer';
import settings from './services/settings/reducer';
import xpacks from './services/xpacks/reducer';
import session from './services/session/reducer';
import features from './services/features/reducer';
import accounts from './services/accounts/reducer';
import flow from './services/flow/reducer';
import dictionaries from './services/dictionaries/reducer';
import dictionaryEntries from './services/dictionaryEntries/reducer';
import dictionaryColumns from './services/dictionaryColumns/reducer';
import entities from './services/entities/reducer';
import colors from './services/colors/reducer';
import entitiesDictionaries from './services/entitiesDictionaries/reducer';
import csv from './services/csv/reducer';
import resources from './services/resources/reducer';
import contexts from './services/contexts/reducer';
import rules from './services/rules/reducer';
import ruleGroups from './services/ruleGroups/reducer';
import ruleEntities from './services/ruleEntities/reducer';
import chat from './services/chat/reducer';
import chatLogs from './services/chatLogs/reducer';
import intents from './services/intents/reducer';
import intentUtterances from './services/intentUtterances/reducer';
import rulesActions from './services/rulesActions/reducer';
import rulesConditions from './services/rulesConditions/reducer';
import entitiesFeatures from './services/entitiesFeatures/reducer';
import testSets from './services/testSets/reducer';
import testSetEntries from './services/testSetEntries/reducer';
import spotlight from './services/spotlight/reducer';
import notifications from './services/notifications/reducer';
import annotations from './services/annotations/reducer';
import versionControl from './services/versionControl/reducer';
import projectTemplates from './services/projectTemplates/reducer';
import newFlow from './newFlow';

export const history = createBrowserHistory();

// FIXME: un-customize history once below is resolved
// - https://github.com/ReactTraining/history/issues/470

const prevHistoryPush = history.push;
let lastLocation = history.location;

history.listen(location => {
  lastLocation = location;
});

history.push = (pathname, state = {}) => {
  if (
    lastLocation === null ||
    pathname !==
      lastLocation.pathname + lastLocation.search + lastLocation.hash ||
    JSON.stringify(state) !== JSON.stringify(lastLocation.state)
  ) {
    prevHistoryPush(pathname, state);
  }
};

// FIXME: customized till here

const reducers = {
  accounts,
  annotations,
  chat,
  chatLogs,
  colors,
  contexts,
  csv,
  flow,
  versionControl,
  dictionaries,
  dictionaryColumns,
  dictionaryEntries,
  entities,
  entitiesDictionaries,
  entitiesFeatures,
  features,
  intents,
  intentUtterances,
  notifications,
  projectTemplates,
  project,
  environments,
  projects,
  resources,
  rules,
  ruleGroups,
  ruleEntities,
  rulesActions,
  rulesConditions,
  session,
  settings,
  spotlight,
  testSets,
  testSetEntries,
  xpacks,
  newFlow,
};

const persistedState = merge(
  combineReducers(reducers)(),
  localStore.loadState(),
  sessionStore.loadState(),
);

export const rootReducer = combineReducers({
  ...reducers,
  router: connectRouter(history),
  form: formReducer,
});

// Enable Redux Devtools in development env only
const composeEnhancers =
  process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'docker'
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
    : compose;

const store = createStore(
  rootReducer,
  persistedState,
  process.env.NLUSTUDIO_SENTRY_DSN
    ? composeEnhancers(
        applyMiddleware(
          reduxCatch((error, getState) => {
            // eslint-disable-next-line
            require('@sentry/browser')
              .configureScope(scope => {
                scope.setExtra('state', getState());
              })
              .captureException(error);
          }),
          thunk,
        ),
      )
    : composeEnhancers(applyMiddleware(routerMiddleware(history), thunk)),
);

store.subscribe(() => {
  const { settings, session, features, newFlow } = store.getState();

  localStore.saveState({
    settings: pick(settings, ['isOpen']),
    session: pick(session, ['data']),
    features: pick(features, ['list.data']),
  });

  sessionStore.saveState({
    // TODO: add here the rest resources
    newFlow: pick(newFlow, [
      'canvases.data',
      'targets.show.data',
      'actions.show.data',
      'fields.list.data',
    ]),
  });
});

export default store;
